import { Quotes, Star, GoogleLogo } from "@phosphor-icons/react";
import { useLang } from "@/hooks/useLang";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";

export function Testimonials({ profile }) {
  const { lang } = useLang();
  const items = profile?.testimonials || [];
  if (!items.length) return null;

  const heading = lang === "da" ? "Det siger dem, jeg har arbejdet med" : "What the people I've worked with say";
  const eyebrow = lang === "da" ? "Udtalelser" : "Testimonials";

  return (
    <section data-testid="testimonials" className="border-t border-border px-6 md:px-12 lg:px-20 py-20">
      <Reveal>
        <div className="label-mono mb-4 text-muted-foreground/60">{eyebrow}</div>
        <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl">{heading}</h2>
      </Reveal>
      <Stagger className="mt-12 grid gap-5 md:grid-cols-2">
        {items.map((tm, i) => (
          <StaggerItem key={i}>
            <figure className="flex h-full flex-col rounded-2xl border border-border bg-card p-8">
              <Quotes size={28} weight="fill" className="text-muted-foreground/40" />
              <blockquote className="mt-5 flex-1 text-lg font-light leading-relaxed text-foreground/90">
                {lang === "da" ? tm.quote_da : tm.quote_en}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-heading font-bold">
                  {tm.author.charAt(0)}
                </div>
                <div>
                  <div className="font-medium">{tm.author}</div>
                  <div className="text-xs text-muted-foreground">
                    {(lang === "da" ? tm.role_da : tm.role_en)}{tm.company ? ` · ${tm.company}` : ""}
                  </div>
                </div>
              </figcaption>
            </figure>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

export function Reviews({ profile }) {
  const { lang } = useLang();
  const reviews = profile?.reviews || [];
  const agg = profile?.aggregate_rating;
  if (!reviews.length) return null;

  const label = lang === "da" ? "Anmeldelser \u00b7 Google & Rilanto" : "Reviews \u00b7 Google & Rilanto";
  const based = lang === "da" ? `Baseret på ${agg?.count} anmeldelser` : `Based on ${agg?.count} reviews`;

  return (
    <section data-testid="reviews" className="border-t border-border px-6 md:px-12 lg:px-20 py-20">
      <div className="grid gap-10 lg:grid-cols-3">
        <Reveal className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <GoogleLogo size={20} weight="bold" /> {label}
            </div>
            <div className="mt-4 flex items-end gap-3">
              <span className="font-heading text-6xl font-black tracking-tighter">{agg?.value?.toFixed(1)}</span>
              <div className="mb-2 flex text-foreground">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} weight="fill" />)}
              </div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">{based}</div>
          </div>
        </Reveal>
        <Stagger className="lg:col-span-2 grid gap-5 sm:grid-cols-2">
          {reviews.map((r, i) => (
            <StaggerItem key={i}>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <div className="flex text-foreground">
                    {[...Array(r.rating)].map((_, j) => <Star key={j} size={14} weight="fill" />)}
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/50">{r.source}</span>
                </div>
                <p className="mt-4 flex-1 text-sm font-light leading-relaxed text-muted-foreground">
                  {lang === "da" ? r.text_da : r.text_en}
                </p>
                <div className="mt-4 border-t border-border pt-3 text-sm font-medium">{r.author}</div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
